"use client";

import { useEffect, useState } from "react";

import { getFeaturedTemplates, getTemplatesForMode, TEMPLATE_REGISTRY, TemplateDefinition } from "@assets/templates";
import type { Mode } from "@demo/constants/mode";

/**
 * Returns the featured-templates list for the active `mode`'s carousel.
 * `getFeaturedTemplates` shuffles its result for per-visit variety, which would
 * mismatch SSR and the first client render — so we seed with a stable,
 * mode-filtered order and apply the shuffle after mount, the same way other
 * media-query-dependent values do here.
 */
export function useFeaturedTemplates(mode: Mode): ReadonlyArray<TemplateDefinition> {
  const [templates, setTemplates] = useState<ReadonlyArray<TemplateDefinition>>(() => stableInitialForMode(mode));
  useEffect(() => {
    setTemplates(getFeaturedTemplates(getTemplatesForMode(TEMPLATE_REGISTRY.templates, mode)));
  }, [mode]);
  return templates;
}

/**
 * Deterministic seed used during SSR + first client render, scoped to `mode`.
 * Uses featured items in registry order with no shuffling, so server and hydration match.
 */
function stableInitialForMode(mode: Mode): ReadonlyArray<TemplateDefinition> {
  const modeTemplates = getTemplatesForMode(TEMPLATE_REGISTRY.templates, mode);
  return modeTemplates.filter((t) => t.featured);
}
